import {Alert, Button, Group, Modal, Select, Stack, Text, TextInput} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileStates from 'FileStates';
import {PermissionIntentFilter} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import SchemaTagTableSchemaSelector from 'metadata/SchemaTagTableSchemaSelector';
import TagTable from 'metadata/TagTable';
import {calcTagTableErrorsByFieldID, useTagTableState} from 'metadata/useTagTableState';
import type {
  TemplateCollectionSelectorQuery,
  TemplateCollectionSelectorQueryVariables,
} from 'notebook/entry-templates/collections/__generated/TemplateCollectionSelectorQuery';
import {TEMPLATE_COLLECTION_SELECTOR_QUERY} from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import notifications from 'notifications';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import * as IntegrationTagUtil from 'schemas/tags/integration/IntegrationTagUtil';
import {getTagsToSet, getTagTableFriendlyTags} from 'schemas/tags/TagUtil';

/* @package - exported for testing */
export const _CONFIGURATION = {
  withinPortal: true,
};

interface SaveAsTemplateModalProps {
  sourceId: string;
  opened: boolean;
  onClose: () => void;
}

export default function SaveAsTemplateModal({
  sourceId,
  opened,
  onClose,
}: SaveAsTemplateModalProps): JSX.Element {
  const [templateName, setTemplateName] = useState('');
  const [templateCollectionId, setTemplateCollectionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {schemaTags, tagSchema, updateSchemaTag, updateTagSchema} = useTagTableState();

  function handleClose(): void {
    setTemplateName('');
    setTemplateCollectionId(null);
    updateTagSchema(null);
    onClose();
  }

  const templateSelections = useBenchlingQuery<
    TemplateCollectionSelectorQuery,
    TemplateCollectionSelectorQueryVariables
  >(TEMPLATE_COLLECTION_SELECTOR_QUERY, {
    variables: {
      searchInput: {permissionsLevel: PermissionIntentFilter.WRITE},
    },
  });

  const templateSelectionsOptions = useMemo(() => {
    if (templateSelections.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return templateSelections.data.templateCollectionsListing.collections.map((c) => ({
      value: c.id,
      label: c.name,
    }));
  }, [templateSelections]);

  const templateNameTrimmed = templateName.trim();
  const tagTableErrorsByFieldId = tagSchema ? calcTagTableErrorsByFieldID(tagSchema, schemaTags) : {};

  const canSubmit = templateNameTrimmed.length > 0 && templateCollectionId != null;

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) {
      throw new Error('Cannot submit form when state is invalid');
    }
    if (isSubmitting) {
      throw new Error('Cannot submit form when already submitting');
    }
    setIsSubmitting(true);

    const tagsToSet =
      BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL && tagSchema
        ? getTagsToSet(schemaTags)
        : undefined;

    try {
      const result = await TemplateDAO.createFromSource({
        sourceId,
        name: templateNameTrimmed,
        templateCollectionApiId: templateCollectionId,
        tagSchemaId: tagSchema?.id,
        schemaTags: tagsToSet,
      });
      FileStates.get().openFolderItem(result.id);
      handleClose();
    } catch (error) {
      notifications.notifyError(error as Response);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Save as template" size="md">
      <Stack spacing="md">
        <Alert>
          <Text>
            When saving as a template, Benchling will save the structure, table configurations, and text
            content. Submittable data and reviews will be removed.
          </Text>
        </Alert>
        <TextInput
          label="Template name"
          data-autofocus={true}
          required={true}
          value={templateName}
          onChange={(e) => {
            setTemplateName(e.currentTarget.value);
          }}
        />
        <Select
          label="Template collection"
          placeholder="Choose collection..."
          required={true}
          data={templateSelectionsOptions}
          value={templateCollectionId}
          disabled={templateSelections.type !== QueryResultType.SUCCESS}
          dropdownPosition="bottom"
          onChange={setTemplateCollectionId}
          withinPortal={_CONFIGURATION.withinPortal}
          searchable={true}
        />
        {BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL && (
          <TagTable
            canRefreshTags={
              ComputedTagUtil.hasComputedTags(getTagTableFriendlyTags(schemaTags)) ||
              IntegrationTagUtil.hasIntegrationTags(getTagTableFriendlyTags(schemaTags))
            }
            emptyMessage="This schema has no fields."
            errorsByFieldId={tagTableErrorsByFieldId}
            tags={schemaTags}
            areTagsEditable={false}
            isLoadingTags={false}
            sequenceParts={null}
            subheaderNode={
              <SchemaTagTableSchemaSelector
                fileType={ModelTypeConstants.ENTRY}
                isSchemaEditable={true}
                isFileRegistered={false}
                onUpdateTagSchema={updateTagSchema}
                tagSchema={tagSchema}
              />
            }
            title={<div>Schema</div>}
            onUpdateTag={updateSchemaTag}
            isEditing={true}
          />
        )}
        <Group position="right">
          <Button disabled={!canSubmit} loading={isSubmitting} onClick={handleSubmit}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
